import React from "react"
import { useSorts } from "../../Common/Hooks/useSorts"
import SI, { Play, Player, Spirit } from "../../Common/SI"
import { PlayerAsSpiritLink } from "../Filtering/PlayerAsSpiritLink"
import { PlayerNameLink } from "../Filtering/PlayerNameLink"
import { SpiritLink } from "../Filtering/SpiritLink"
import Sorting, { Sort } from "../Sorting"
import { SortToggle } from "../SortToggle"

export interface PlayerSpiritTableProps {
	players: Player[]
	plays: Play[]
}

export const PlayerSpiritTable = React.memo(function (props: PlayerSpiritTableProps): JSX.Element {

	const [mode, setMode] = React.useState<'lastPlayed' | 'playCount'>('playCount')

	const table = 'PlayerSpirit'

	const { sorts } = useSorts()

	const sort = sorts.get(table)
	let orderedSpirits: Spirit[] | undefined = undefined

	const lastPlayedBySpiritByPlayer: Map<Player, Map<Spirit, string>> = new Map()

	switch (mode) {
		case 'lastPlayed':
			populateLastPlayedStats(lastPlayedBySpiritByPlayer, props.players, props.plays)
			orderedSpirits = sortByLastPlayed(sort, lastPlayedBySpiritByPlayer, props.players)
			break

		case 'playCount':
			orderedSpirits = sortByPlayCount(sort, props.plays)
			break
	}


	if (!orderedSpirits) {

		// No valid sort order. Put the unplayed ones at the end.
		const spiritPlayed = SI.spirits.map(spirit => {
			return {
				spirit,
				spiritName: spirit.nickname,
				expansionOrder: SI.expansionOrder.get(spirit.expansion),
				played: props.plays.some(play => play.spirits.some(playedSpirit => playedSpirit.spirit.name === spirit.name))
			}
		})
		Sorting.sort(spiritPlayed, { field: 'played', ascending: false }, 'expansionOrder', 'spiritName')
		orderedSpirits = spiritPlayed.map(sp => sp.spirit)

	}

	const onChangeMode = (event: any) => {
		setMode(event.target.value)
	}

	return (
		<div>
			<table className='stats player-spirit'>
				<thead>
					<tr>
						<th onChange={onChangeMode}>
							<div>
								<input type="radio" name="PlayerSpiritTableMode" value="playCount" id="PlayerSpiritTableModePlayCount" defaultChecked />
								<label htmlFor="PlayerSpiritTableModePlayCount">Play count</label>
							</div>
							<div>
								<input type="radio" name="PlayerSpiritTableMode" value="lastPlayed" id="PlayerSpiritTableModeLastPlayed" />
								<label htmlFor="PlayerSpiritTableModeLastPlayed">Last played</label>
							</div>
						</th>
						{
							orderedSpirits.map(spirit =>
								<th key={spirit.name}>
									<span className='sideways'>
										<SpiritLink spirit={spirit} />
									</span>
								</th>
							)
						}
						<th>
							<span className='sideways'>Total plays</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{
						props.players.map(player =>
							<tr key={player.name}>
								<th>
									<SortToggle table={table} field={player.name} canClear={true} />
									<PlayerNameLink player={player} />
								</th>
								{
									orderedSpirits!.map(spirit =>
										<td key={spirit.name}>
											<PlayerAsSpiritLink
												player={player}
												spirit={spirit}>
												<React.Fragment>
													{
														mode === 'lastPlayed'
															? formatDate(lastPlayedBySpiritByPlayer.get(player)?.get(spirit))
															: props.plays.filter(play => play.spirits.some(playedSpirit => playedSpirit.spirit.name === spirit.name && playedSpirit.player.name === player.name)).length || ''
													}
												</React.Fragment>
											</PlayerAsSpiritLink>
										</td>
									)
								}
								<td>
									{props.plays.filter(play => play.players.some(playedPlayer => playedPlayer.name === player.name)).length}
								</td>
							</tr>
						)
					}
					{
						<tr>
							<th>Play count</th>
							{
								orderedSpirits.map(spirit =>
									<td key={spirit.name}>
										{props.plays.filter(play => play.spirits.some(playedSpirit => playedSpirit.spirit.name === spirit.name)).length || ''}
									</td>
								)
							}
							<td></td>
						</tr>
					}
				</tbody>
			</table>
		</div>
	)
})

function formatDate(dateTime: string | undefined): string {
	if (!dateTime || dateTime.length < 8) {
		return ''
	}

	const day = dateTime.substring(8, 10)
	const month = dateTime.substring(5, 7)
	const year = dateTime.substring(2, 4)

	return `${day}/${month}/${year}`
}

function populateLastPlayedStats(
	lastPlayedBySpiritByPlayer: Map<Player, Map<Spirit, string>>,
	players: Player[],
	plays: Play[]) {

	for (const player of players) {
		const lastPlayedBySpirit: Map<Spirit, string> = new Map()
		lastPlayedBySpiritByPlayer.set(player, lastPlayedBySpirit)
		for (const spirit of SI.spirits) {
			lastPlayedBySpirit.set(spirit, '')
		}
	}

	for (const play of plays) {
		for (const playedSpirit of play.spirits) {
			const lastPlayedBySpirit = lastPlayedBySpiritByPlayer.get(playedSpirit.player)
			if (!lastPlayedBySpirit) {
				continue
			}

			if (play.dateTimeString > (lastPlayedBySpirit.get(playedSpirit.spirit) || '')) {
				lastPlayedBySpirit.set(playedSpirit.spirit, play.dateTimeString)
			}
		}
	}

}

function sortByLastPlayed(
	sort: Sort | undefined,
	lastPlayedBySpiritByPlayer: Map<Player, Map<Spirit, string>>,
	players: Player[]): Spirit[] | undefined {

	if (!sort) {
		return undefined
	}

	// The sort field is a player name
	const matchingPlayers = players.filter(player => player.name === sort.field)
	if (!matchingPlayers || matchingPlayers.length === 0) {
		return undefined
	}

	const player = matchingPlayers[0]
	const lastPlayedBySpirit = lastPlayedBySpiritByPlayer.get(player)

	const lastPlays = SI.spirits.map(spirit => {
		return {
			spirit,
			spiritName: spirit.nickname,
			expansionOrder: SI.expansionOrder.get(spirit.expansion),
			lastPlayed: lastPlayedBySpirit?.get(spirit) || null,
		}
	})
	Sorting.sort(lastPlays, { field: 'lastPlayed', ascending: sort.ascending }, 'expansionOrder', 'spiritName')
	return lastPlays.map(lp => lp.spirit)

}

function sortByPlayCount(
	sort: Sort | undefined,
	plays: Play[]): Spirit[] | undefined {

	if (!sort) {
		return undefined
	}

	// The sort field is a player name
	const spiritPlays = SI.spirits.map(spirit => {
		return {
			spirit,
			spiritName: spirit.nickname,
			expansionOrder: SI.expansionOrder.get(spirit.expansion),
			plays: plays.filter(play => play.spirits.some(playedSpirit => playedSpirit.spirit.name === spirit.name && playedSpirit.player.name === sort.field)).length || null
		}
	})
	Sorting.sort(spiritPlays, { field: 'plays', ascending: sort.ascending }, 'expansionOrder', 'spiritName')
	return spiritPlays.map(sp => sp.spirit)

}
